/**
 * Per-user outbox for chat messages awaiting a successful send.
 *
 * Storage choice: localStorage.
 *
 * - We need synchronous reads during render (the ChatPage bubbles
 *   need to know "is this message pending/failed?" without an async
 *   round-trip). IndexedDB is async-only and would force us to
 *   plumb loading states through every consumer.
 * - The data shape is tiny (a handful of fields per message, capped
 *   at 50 items per user) — well inside the 5MB localStorage budget
 *   even for power users.
 * - localStorage's mutex-per-origin behaviour means we can't
 *   trivially share between tabs, but the existing chat session is
 *   already single-tab (Clerk's sign-in flow doesn't fan out
 *   between tabs and the persona context is per-tab too).
 *
 * Key layout: `ti:outbox:{userId}` — identity-scoped so signing in
 * as a different account on the same device can never resurface
 * the previous user's pending messages. This mirrors the
 * `ti:chatHistory:{userId}` pattern locked in May 2026 after the
 * cross-account chat-bleed fix.
 *
 * Versioned blob format: `{ version: 1, items: OutboxItem[] }`.
 * The version literal future-proofs us against schema bumps without
 * blowing away existing users' pending messages on deploy.
 *
 * Backoff (sub-commit 4b, June 2026):
 *
 * - `nextRetryAt` is an optional ISO timestamp set by the drain
 *   effect on failed-retryable transitions. It defaults to null,
 *   meaning "due now" — backwards-compatible with v1 blobs that
 *   pre-date the field.
 * - `computeNextRetryAt(attemptCount)` produces the schedule
 *   `min(2^attemptCount, 30)` seconds in the future. Caller passes
 *   the NEW attempt count (after increment), so a first retry
 *   waits 2s, a second waits 4s, capped at 30s.
 * - `getDueRetryableItems(userId)` is the choke-point the drain
 *   effect uses: queued OR failed-retryable, AND nextRetryAt null
 *   or in the past.
 *
 * Reactive reads (sub-commit 4b):
 *
 * - `subscribeOutbox` / `getOutboxSnapshot` form a useSyncExternal-
 *   Store-compatible pair. Every mutation bumps an internal version
 *   counter and notifies subscribed listeners; the snapshot getter
 *   memoises by `(userId, version)` so React's Object.is bail-out
 *   works without re-reading localStorage on every render. See
 *   useOutbox.ts for the consumer hook.
 */

import { z } from 'zod';

const STORAGE_PREFIX = 'ti:outbox:';
const STORAGE_VERSION = 1;
const MAX_ITEMS_PER_USER = 50;

const SUPPORTED_LANGUAGES = ['en', 'fr', 'de', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const outboxStatusSchema = z.enum([
  'queued',
  'sending',
  'failed-retryable',
  'failed-permanent',
]);
export type OutboxStatus = z.infer<typeof outboxStatusSchema>;

export const outboxItemSchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  text: z.string().min(1).max(4000),
  dateLocal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  language: z.enum(SUPPORTED_LANGUAGES),
  clientNonce: z.string().uuid(),
  status: outboxStatusSchema,
  attemptCount: z.number().int().nonnegative(),
  lastError: z.string().nullable(),
  // ISO datetime when this item next becomes eligible for a retry,
  // or null meaning "due immediately". `.default(null)` makes the
  // field optional on input so existing v1 blobs (written before
  // the field existed) still parse cleanly — Zod backfills null on
  // read and the next write persists it explicitly.
  nextRetryAt: z.string().datetime().nullable().default(null),
  createdAt: z.string().datetime(),
});
export type OutboxItem = z.infer<typeof outboxItemSchema>;

const outboxBlobV1Schema = z.object({
  version: z.literal(STORAGE_VERSION),
  items: z.array(outboxItemSchema),
});

type EnqueueParams = {
  text: string;
  dateLocal: string;
  language: SupportedLanguage;
};

type UpdatePatch = {
  status?: OutboxStatus;
  attemptCount?: number;
  lastError?: string | null;
  nextRetryAt?: string | null;
};

// ---------------------------------------------------------------
// Pub/sub + snapshot cache for useSyncExternalStore.
// ---------------------------------------------------------------

type Listener = () => void;

const listeners = new Set<Listener>();

// Bumped on every mutation. The snapshot cache keys on this value
// so we can return a stable reference for unchanged state (React's
// Object.is bail-out depends on it) while still re-reading
// localStorage when something actually changes.
let snapshotVersion = 0;

let cachedSnapshot: {
  version: number;
  userId: string | null;
  items: OutboxItem[];
} | null = null;

function notify(): void {
  // A listener throwing must not break the outbox operation that
  // triggered the notify nor block other listeners. We silently
  // swallow — useSyncExternalStore listeners are expected to be
  // pure schedule-a-rerender callbacks and shouldn't throw.
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // intentional
    }
  }
}

function invalidate(): void {
  snapshotVersion += 1;
  notify();
}

/**
 * Subscribe to outbox-state changes. The listener is invoked
 * after every mutation (enqueue/update/remove/clear). Returns an
 * unsubscribe function. Shape matches useSyncExternalStore's
 * `subscribe` argument.
 */
export function subscribeOutbox(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Memoised snapshot getter for useSyncExternalStore. Returns the
 * same reference until a mutation bumps `snapshotVersion` OR the
 * userId changes — both are required for React to bail out of
 * re-renders correctly.
 *
 * Caches a single snapshot at a time (we only ever have one signed-
 * in user per session). When userId flips to null on sign-out,
 * the cache key mismatches and the next call returns an empty array.
 */
export function getOutboxSnapshot(
  userId: string | null | undefined,
): OutboxItem[] {
  const key = userId && userId.length > 0 ? userId : null;
  if (
    cachedSnapshot !== null &&
    cachedSnapshot.userId === key &&
    cachedSnapshot.version === snapshotVersion
  ) {
    return cachedSnapshot.items;
  }
  const items = key !== null ? readOutbox(key) : [];
  cachedSnapshot = { version: snapshotVersion, userId: key, items };
  return items;
}

/**
 * Test-only reset for module-level state. The outbox lives in
 * module scope (listeners Set, snapshot cache, version counter)
 * which doesn't survive a `localStorage.clear()` — tests need to
 * call this in beforeEach alongside the localStorage clear.
 */
export function _resetOutboxForTests(): void {
  listeners.clear();
  snapshotVersion = 0;
  cachedSnapshot = null;
}

// ---------------------------------------------------------------
// Backoff helper.
// ---------------------------------------------------------------

const BACKOFF_CAP_SECONDS = 30;

/**
 * Compute the ISO timestamp at which a retryable item becomes due
 * again, given the *new* attempt count (post-increment). Schedule
 * is `min(2^attemptCount, 30)` seconds from now — so the first
 * retry waits 2s, the second 4s, the third 8s, capping at 30s.
 *
 * The caller is expected to call this only for failed-retryable
 * transitions; failed-permanent items never auto-retry, so a next
 * retry time is meaningless for them.
 */
export function computeNextRetryAt(attemptCount: number): string {
  const seconds = Math.min(2 ** attemptCount, BACKOFF_CAP_SECONDS);
  return new Date(Date.now() + seconds * 1000).toISOString();
}

// ---------------------------------------------------------------
// Persistence primitives.
// ---------------------------------------------------------------

function keyFor(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function loadItems(userId: string): OutboxItem[] {
  const raw = localStorage.getItem(keyFor(userId));
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  const result = outboxBlobV1Schema.safeParse(parsed);
  if (!result.success) return [];
  return result.data.items;
}

function saveItems(userId: string, items: OutboxItem[]): void {
  const blob = { version: STORAGE_VERSION, items };
  localStorage.setItem(keyFor(userId), JSON.stringify(blob));
}

// ---------------------------------------------------------------
// Public API.
// ---------------------------------------------------------------

/**
 * Read the current outbox for a user. Returns an empty array for
 * missing/blank userIds (Clerk's `useAuth()` returns null while
 * the session is still loading) and for any persisted blob that
 * fails schema validation — better to drop a corrupted store than
 * crash on render.
 */
export function readOutbox(userId: string | null | undefined): OutboxItem[] {
  if (!userId) return [];
  return loadItems(userId);
}

/**
 * Enqueue a new chat-send to the outbox. Mints a fresh `id`,
 * `clientNonce` (UUID v4 — the idempotency token the API uses to
 * deduplicate retries), and `createdAt`. Status starts as `queued`
 * with attemptCount 0 and no error. nextRetryAt starts null
 * (meaning "due immediately" to the drain effect).
 *
 * When the per-user store is at capacity, drops the oldest item.
 */
export function enqueueOutboxItem(
  userId: string,
  params: EnqueueParams,
): OutboxItem {
  const item: OutboxItem = {
    id: `out_${crypto.randomUUID()}`,
    userId,
    text: params.text,
    dateLocal: params.dateLocal,
    language: params.language,
    clientNonce: crypto.randomUUID(),
    status: 'queued',
    attemptCount: 0,
    lastError: null,
    nextRetryAt: null,
    createdAt: new Date().toISOString(),
  };

  const existing = loadItems(userId);
  const next = [...existing, item];
  // Drop oldest on overflow — newest enqueue is the most important
  // to retain since the user just hit Send on it.
  const trimmed =
    next.length > MAX_ITEMS_PER_USER
      ? next.slice(next.length - MAX_ITEMS_PER_USER)
      : next;
  saveItems(userId, trimmed);
  invalidate();
  return item;
}

/**
 * Patch an outbox item in place. Only the fields named in `patch`
 * are touched — clientNonce, text, createdAt and other immutable
 * fields are explicitly preserved (rotating clientNonce mid-retry
 * would defeat the idempotency contract).
 *
 * No-op for unknown ids — a stale reference (e.g. an item that
 * succeeded and was removed between the consumer reading state
 * and dispatching the update) should silently drop, not throw.
 */
export function updateOutboxItem(
  userId: string,
  itemId: string,
  patch: UpdatePatch,
): void {
  const items = loadItems(userId);
  const idx = items.findIndex((i) => i.id === itemId);
  if (idx === -1) return;
  const current = items[idx];
  if (!current) return;
  const updated: OutboxItem = {
    ...current,
    ...(patch.status !== undefined ? { status: patch.status } : {}),
    ...(patch.attemptCount !== undefined
      ? { attemptCount: patch.attemptCount }
      : {}),
    ...(patch.lastError !== undefined ? { lastError: patch.lastError } : {}),
    ...(patch.nextRetryAt !== undefined
      ? { nextRetryAt: patch.nextRetryAt }
      : {}),
  };
  const next = [...items];
  next[idx] = updated;
  saveItems(userId, next);
  invalidate();
}

/**
 * Remove an item by id. No-op for unknown ids.
 */
export function removeOutboxItem(userId: string, itemId: string): void {
  const items = loadItems(userId);
  const next = items.filter((i) => i.id !== itemId);
  if (next.length === items.length) return;
  saveItems(userId, next);
  invalidate();
}

/**
 * Clear all items for a user. Called from sign-out flows alongside
 * the broader storage purge.
 */
export function clearOutbox(userId: string | null | undefined): void {
  if (!userId) return;
  localStorage.removeItem(keyFor(userId));
  invalidate();
}

/**
 * Return all items eligible for retry, regardless of backoff
 * timing — used by status indicators that want to show "X
 * messages waiting" without filtering by due-ness. The drain
 * effect should use getDueRetryableItems instead.
 */
export function getRetryableItems(
  userId: string | null | undefined,
): OutboxItem[] {
  if (!userId) return [];
  return loadItems(userId).filter(
    (i) => i.status === 'queued' || i.status === 'failed-retryable',
  );
}

/**
 * Return items the drain effect should act on right now: queued
 * OR failed-retryable, AND nextRetryAt is null (no backoff) or in
 * the past (backoff elapsed). Insertion order preserved.
 */
export function getDueRetryableItems(
  userId: string | null | undefined,
): OutboxItem[] {
  if (!userId) return [];
  const now = Date.now();
  return loadItems(userId).filter((i) => {
    if (i.status !== 'queued' && i.status !== 'failed-retryable') return false;
    if (i.nextRetryAt === null) return true;
    return new Date(i.nextRetryAt).getTime() <= now;
  });
}
